import { useState, useEffect, useRef, useCallback } from 'react';
import { notificationApi } from '../services/api/notificationApi';
import type { Notification as AppNotification } from '../services/api/notificationApi';

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

const PUSH_PREF_KEY = 'carboncare_push_notifications_enabled';
/** How often to poll for new notifications (ms) */
const POLL_INTERVAL_MS = 30000;

interface PushStatePatch {
  enabled?: boolean;
  permission?: PushPermissionState;
}
type PushStateListener = (patch?: PushStatePatch) => void;

/**
 * Every mounted instance of this hook keeps its own React state, so a
 * localStorage write on the Settings page would otherwise leave the app-wide
 * poller stuck on its stale value until a full page reload. Instances register
 * here and are told about preference/permission changes as they happen.
 */
const stateListeners = new Set<PushStateListener>();
/** No patch means "re-read everything from storage and the browser". */
const broadcastPushState = (patch?: PushStatePatch): void => {
  stateListeners.forEach(listener => listener(patch));
};

/** Read the push preference, swallowing storage errors. */
const readPushPref = (): boolean => {
  try { return localStorage.getItem(PUSH_PREF_KEY) === 'true'; } catch { return false; }
};

/** Persist the push preference, swallowing storage errors. */
const persistPushPref = (enabled: boolean): void => {
  try { localStorage.setItem(PUSH_PREF_KEY, String(enabled)); } catch { /* ignore */ }
  broadcastPushState({ enabled });
};

interface UsePushNotificationsOptions {
  /** Only the app-wide poller should poll; other consumers just read/toggle state. */
  enablePolling?: boolean;
}

/**
 * Custom hook to manage browser push notification permission and preference.
 *
 * - Reads the current browser Notification permission on mount.
 * - Polls the notification API every 30 s when notifications are enabled.
 * - Fires a browser Notification for each new unread notification (once per ID).
 * - `isEnabled` is true only when the user opted in AND the browser has granted permission.
 * - Persists the user's preference in localStorage across page reloads, and keeps
 *   every mounted instance in sync so toggling on Settings takes effect immediately.
 */
export const usePushNotifications = (options: UsePushNotificationsOptions = {}) => {
  const { enablePolling = true } = options;
  const isSupported =
    globalThis.window !== undefined && 'Notification' in globalThis.window;

  const getBrowserPermission = (): PushPermissionState => {
    if (!isSupported) return 'unsupported';
    return Notification.permission;
  };

  const [browserPermission, setBrowserPermission] =
    useState<PushPermissionState>(getBrowserPermission);

  // Whether the user flipped the toggle ON (stored preference)
  const [userWantsEnabled, setUserWantsEnabled] = useState<boolean>(readPushPref);

  // Track which notification IDs have already triggered a browser notification
  const shownIdsRef = useRef<Set<string>>(new Set());
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isEnabled = isSupported && userWantsEnabled && browserPermission === 'granted';

  // ─── Keep every instance (and every tab) in sync ─────────────────────────
  useEffect(() => {
    const syncState: PushStateListener = (patch) => {
      if (!patch) {
        setBrowserPermission(getBrowserPermission());
        setUserWantsEnabled(readPushPref());
        return;
      }
      if (patch.permission !== undefined) setBrowserPermission(patch.permission);
      if (patch.enabled !== undefined) setUserWantsEnabled(patch.enabled);
    };
    const onFocus = () => syncState();
    const onStorage = (event: StorageEvent) => {
      if (event.key === PUSH_PREF_KEY) syncState();
    };

    stateListeners.add(syncState);
    globalThis.addEventListener('focus', onFocus);
    globalThis.addEventListener('storage', onStorage);
    return () => {
      stateListeners.delete(syncState);
      globalThis.removeEventListener('focus', onFocus);
      globalThis.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported]);

  // ─── Show a browser notification for a single app notification ───────────
  const showBrowserNotification = useCallback(
    (notification: AppNotification) => {
      if (!isSupported || globalThis.Notification.permission !== 'granted') return;
      try {
        const title = notification.message || 'CarbonCare Notification';
        console.log('[PushNotifications] Firing browser notification:', title);
        const n = new globalThis.Notification(title, {
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notification.id,          // deduplicates if OS supports it
          body: (() => {
            if (notification.metadata?.exportType === 'shipments_excel') {
              return `File: ${notification.metadata.fileName || 'Analytics Export'}`;
            }
            if (notification.metadata?.fileName) {
              return `File: ${notification.metadata.fileName}`;
            }
            return undefined;
          })(),
        });

        // Clicking the browser notification focuses the tab
        n.onclick = () => {
          globalThis.focus();
          if (notification.metadata?.exportType === 'shipments_excel') {
            globalThis.location.href = '/analytics';
          } else if (notification.metadata?.fileId) {
            globalThis.location.href = '/files/calculated';
          }
        };
      } catch (err) {
        console.error('[PushNotifications] Failed to show notification:', err);
      }
    },
    [isSupported]
  );

  // ─── Single poll: fetch unread, show any we haven't shown yet ────────────
  const poll = useCallback(async () => {
    if (!isSupported || Notification.permission !== 'granted') return;
    try {
      const unread = await notificationApi.getUnreadNotifications();
      unread.forEach((notif: AppNotification) => {
        if (!shownIdsRef.current.has(notif.id)) {
          shownIdsRef.current.add(notif.id);
          showBrowserNotification(notif);
        }
      });
    } catch (err) {
      // Silently ignore — polling errors shouldn't break the app
      console.warn('[PushNotifications] Poll error:', err);
    }
  }, [isSupported, showBrowserNotification]);

  // ─── Start / stop polling based on isEnabled ─────────────────────────────
  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (isEnabled && enablePolling) {
      // Immediate first poll, then regular interval
      poll();
      pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isEnabled, enablePolling, poll]);

  // ─── Request browser permission ───────────────────────────────────────────
  const requestPermission = useCallback(async (): Promise<PushPermissionState> => {
    if (!isSupported) return 'unsupported';
    try {
      const result = await Notification.requestPermission();
      const state = result as PushPermissionState;
      setBrowserPermission(state);
      broadcastPushState({ permission: state });
      return state;
    } catch (err) {
      console.error('[PushNotifications] Permission request failed:', err);
      setBrowserPermission('denied');
      broadcastPushState({ permission: 'denied' });
      return 'denied';
    }
  }, [isSupported]);

  /**
   * Toggle the user's push-notification preference.
   * When enabling: fires a permission request if not yet granted.
   * Returns the final effective enabled state.
   */
  const togglePushNotifications = useCallback(
    async (enable: boolean): Promise<boolean> => {
      if (!isSupported) return false;

      if (!enable) {
        setUserWantsEnabled(false);
        persistPushPref(false);
        return false;
      }

      let permission = getBrowserPermission();
      if (permission === 'default') {
        permission = await requestPermission();
      }

      const granted = permission === 'granted';
      setUserWantsEnabled(granted);
      persistPushPref(granted);
      if (granted) {
        // Clear seen IDs so the user immediately gets any pending unread ones
        shownIdsRef.current.clear();
      }
      return granted;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSupported, requestPermission]
  );

  return {
    isSupported,
    browserPermission,
    userWantsEnabled,
    isEnabled,
    togglePushNotifications,
  };
};
